import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import styled, { useTheme } from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * AppointmentNumberCard
 * ------------------------------------------------------------------------------------------------- */

type AppointmentNumberRootProps = ComponentPropsWithoutRef<'button'> & {
  onCopy: () => void
}

const AppointmentNumberRoot = ({ children, onCopy, ...props }: AppointmentNumberRootProps) => {
  // No aria-label: the accessible name comes from the card content, so the number itself is announced.
  return (
    <StCard type='button' onClick={onCopy} {...props}>
      {children}
    </StCard>
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * AppointmentNumberCard.Label
 * ------------------------------------------------------------------------------------------------- */

type AppointmentNumberLabelProps = ComponentPropsWithoutRef<'span'>

const AppointmentNumberLabel = ({ children, ...props }: AppointmentNumberLabelProps) => {
  const theme = useTheme()

  return (
    <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker} {...props}>
      {children}
    </Span>
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * AppointmentNumberCard.Value
 * ------------------------------------------------------------------------------------------------- */

type AppointmentNumberValueProps = ComponentPropsWithoutRef<'span'>

const AppointmentNumberValue = ({ children, ...props }: AppointmentNumberValueProps) => {
  return <StNumber {...props}>{children}</StNumber>
}

/* ------------------------------------------------------------------------------------------------- *
 * AppointmentNumberCard.Icon
 * ------------------------------------------------------------------------------------------------- */

type AppointmentNumberIconProps = Omit<ComponentPropsWithoutRef<typeof FontAwesomeIcon>, 'icon'>

const AppointmentNumberIcon = (props: AppointmentNumberIconProps) => {
  return (
    <StCopyIcon
      icon={['far', 'copy']}
      fontSize={20}
      title={t({
        id: 'appointments.detail.appointment-number.copy',
        message: 'Afspraaknummer kopiëren',
      })}
      {...props}
    />
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * AppointmentNumberCard.Hint
 * ------------------------------------------------------------------------------------------------- */

type AppointmentNumberHintProps = ComponentPropsWithoutRef<'span'>

const AppointmentNumberHint = ({ children, ...props }: AppointmentNumberHintProps) => {
  const theme = useTheme()

  return (
    <StHint type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker} {...props}>
      {children}
    </StHint>
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const AppointmentNumberCard = Object.assign(AppointmentNumberRoot, {
  Label: AppointmentNumberLabel,
  Value: AppointmentNumberValue,
  Icon: AppointmentNumberIcon,
  Hint: AppointmentNumberHint,
})

export { AppointmentNumberCard }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StCard = styled.button`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.UI.SpacingPx.Space1};
    width: 100%;
    padding: ${theme.UI.SpacingPx.Space5};
    border: 1px solid ${theme.Colors.Neutrals.Light};
    border-radius: ${theme.UI.BorderRadius.Large}px;
    background-color: ${theme.Colors.Neutrals.Lighter};
    text-align: left;
    font: inherit;
    color: inherit;
    cursor: pointer;
  `}
`

const StNumber = styled.span`
  ${({ theme }) => theme.Typography.Heading2}
  color: ${({ theme }) => theme.Colors.Theme.Primary};
`

const StCopyIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.Colors.Neutrals.Dark};
`

const StHint = styled(Span)`
  margin-top: ${({ theme }) => theme.UI.SpacingPx.Space2};
`
